/**
 * Streamer MyPage Modal Component
 * Modal version of streamer profile editing for use during broadcast
 * Allows profile image and profile text editing without leaving the broadcast screen
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import type { StreamerProfileUpdate } from '../lib/types';
import { resolveMediaUrl } from '../lib/media-url';
import {
    PROFILE_IMAGE_REVIEW_MESSAGES,
    profileImageReviewState,
    profileSaveErrorMessage,
    type ProfileImageReviewState,
} from '../lib/moderation-messages';

interface StreamerMyPageModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function StreamerMyPageModal({ isOpen, onClose }: StreamerMyPageModalProps) {
    const { user } = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [imageReview, setImageReview] = useState<ProfileImageReviewState>(null);
    const [imageCacheBuster, setImageCacheBuster] = useState<number>(Date.now());
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Profile form state
    const [displayName, setDisplayName] = useState('');
    const [age, setAge] = useState('');
    const [height, setHeight] = useState('');
    const [hobbies, setHobbies] = useState('');
    const [favoriteFood, setFavoriteFood] = useState('');
    const [features, setFeatures] = useState('');
    const [bio, setBio] = useState('');

    // Fetch profile when modal opens
    useEffect(() => {
        const fetchProfile = async () => {
            if (!isOpen || !user?.username) {
                return;
            }

            setIsLoadingProfile(true);
            try {
                const profile = await apiClient.getStreamerProfile(user.username);
                if (profile.profile_image_url) {
                    const imageUrl = resolveMediaUrl(profile.profile_image_url);
                    setProfileImageUrl(imageUrl);
                }
                setDisplayName(profile.display_name || user.username || '');
                setAge(profile.age ? String(profile.age) : '');
                setHeight(profile.height ? String(profile.height) : '');
                setHobbies(profile.hobbies || '');
                setFavoriteFood(profile.favorite_food || '');
                setFeatures(profile.features || '');
                setBio(profile.bio || '');
            } catch (error) {
                console.error('Failed to fetch profile:', error);
                setDisplayName(user.username || '');
            } finally {
                setIsLoadingProfile(false);
            }
        };
        fetchProfile();
    }, [isOpen, user?.username]);

    // プロフィール画像の承認状態（承認待ち・非承認）
    useEffect(() => {
        if (!isOpen || !user?.username) return;
        apiClient.getMyModerationStatus()
            .then((status) => setImageReview(profileImageReviewState(status)))
            .catch((error) => console.error('Failed to fetch moderation status:', error));
    }, [isOpen, user?.username]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSaveError(null);
            setSaveSuccess(false);
            setUploadError(null);
            setPreviewUrl(null);
        }
    }, [isOpen]);

    const handleSaveProfile = async () => {
        if (!user?.username) return;

        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            const ageNum = age ? parseInt(age, 10) : undefined;
            const heightNum = height ? parseInt(height, 10) : undefined;

            const updateData: StreamerProfileUpdate = {
                display_name: displayName || undefined,
                age: ageNum && !isNaN(ageNum) ? ageNum : undefined,
                height: heightNum && !isNaN(heightNum) ? heightNum : undefined,
                hobbies: hobbies || undefined,
                favorite_food: favoriteFood || undefined,
                features: features || undefined,
                bio: bio || undefined,
            };

            await apiClient.updateStreamerProfile(user.username, updateData);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to save profile:', error);
            setSaveError(profileSaveErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setUploadError('ファイルサイズは5MB以下にしてください');
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            setUploadError('JPG、PNG、WebP、GIF形式のみ対応しています');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        setIsUploading(true);
        setUploadError(null);

        try {
            // 画像は運営の承認後に公開される。表示は承認済みの画像のまま
            const response = await apiClient.uploadProfileImage(file);
            setProfileImageUrl(response.profile_image_url ? resolveMediaUrl(response.profile_image_url) : null);
            setImageCacheBuster(Date.now());
            setPreviewUrl(null);
            setImageReview('pending');
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadError('アップロードに失敗しました');
            setPreviewUrl(null);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const getImageUrlWithCacheBuster = (url: string | null) => {
        if (!url) return null;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}t=${imageCacheBuster}`;
    };
    const displayImageUrl = previewUrl || getImageUrlWithCacheBuster(profileImageUrl);

    const DefaultUserIcon = ({ className }: { className?: string }) => (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-pink-100 to-pink-200 px-6 py-4 flex items-center justify-between border-b border-pink-300 sticky top-0">
                    <h2 className="text-xl font-bold text-pink-900">マイページ</h2>
                    <button
                        onClick={onClose}
                        className="text-pink-700 hover:text-pink-900 transition p-1"
                    >
                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {isLoadingProfile ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                        </div>
                    ) : (
                        <>
                            {/* Profile Image Section */}
                            <div className="flex items-start gap-6 mb-6">
                                <div className="flex flex-col items-center">
                                    <div className="relative group">
                                        <div
                                            className="w-24 h-24 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden cursor-pointer"
                                            onClick={handleImageClick}
                                        >
                                            {isUploading ? (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500"></div>
                                                </div>
                                            ) : displayImageUrl ? (
                                                <img
                                                    src={displayImageUrl}
                                                    alt={user?.username || 'プロフィール'}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                                    <DefaultUserIcon className="w-12 h-12 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            className="absolute inset-0 rounded-lg bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                            onClick={handleImageClick}
                                        >
                                            <span className="text-white text-xs font-semibold">変更</span>
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">クリックして変更</p>
                                    {imageReview && (
                                        <p
                                            className={`mt-2 max-w-[160px] text-xs ${imageReview === 'pending' ? 'text-yellow-700' : 'text-red-600'}`}
                                            role="status"
                                        >
                                            {PROFILE_IMAGE_REVIEW_MESSAGES[imageReview]}
                                        </p>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                                        {displayName || user?.username || '配信者'}
                                    </h3>
                                    {uploadError && (
                                        <p className="text-red-500 text-sm">{uploadError}</p>
                                    )}
                                </div>
                            </div>

                            {/* Status Messages */}
                            {saveError && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
                                    {saveError}
                                </div>
                            )}
                            {saveSuccess && (
                                <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md text-sm">
                                    プロフィールを保存しました
                                </div>
                            )}

                            {/* Profile Form */}
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-1">
                                        <label className="block font-semibold text-gray-700 text-sm">配信者名</label>
                                        <input
                                            type="text"
                                            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                                            value={displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block font-semibold text-gray-700 text-sm">年齢</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min="0"
                                                max="150"
                                                className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 text-right"
                                                value={age}
                                                onChange={(e) => setAge(e.target.value)}
                                                placeholder="24"
                                            />
                                            <span className="text-gray-700 text-sm flex-shrink-0">歳</span>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block font-semibold text-gray-700 text-sm">身長</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min="0"
                                                max="300"
                                                className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 text-right"
                                                value={height}
                                                onChange={(e) => setHeight(e.target.value)}
                                                placeholder="162"
                                            />
                                            <span className="text-gray-700 text-sm flex-shrink-0">cm</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-1">
                                        <label className="block font-semibold text-gray-700 text-sm">趣味</label>
                                        <input
                                            type="text"
                                            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                                            value={hobbies}
                                            onChange={(e) => setHobbies(e.target.value)}
                                            placeholder="例: 読書、映画鑑賞"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block font-semibold text-gray-700 text-sm">好きな食べ物</label>
                                        <input
                                            type="text"
                                            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                                            value={favoriteFood}
                                            onChange={(e) => setFavoriteFood(e.target.value)}
                                            placeholder="例: イタリアン、スイーツ"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block font-semibold text-gray-700 text-sm">チャームポイント</label>
                                        <input
                                            type="text"
                                            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                                            value={features}
                                            onChange={(e) => setFeatures(e.target.value)}
                                            placeholder="例: 笑顔、話しやすさ"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="block font-semibold text-gray-700 text-sm">自己紹介</label>
                                    <textarea
                                        className="w-full p-2.5 border border-gray-300 rounded-md text-sm min-h-[80px] resize-y focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                                        value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                        placeholder="自己紹介を入力してください"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-white border border-gray-300 text-gray-600 font-semibold rounded-md hover:bg-gray-50 transition text-sm"
                        disabled={isSaving}
                    >
                        閉じる
                    </button>
                    <button
                        onClick={handleSaveProfile}
                        disabled={isSaving || isLoadingProfile}
                        className="px-5 py-2.5 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-md transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? '保存中...' : '更新する'}
                    </button>
                </div>
            </div>
        </div>
    );
}
